/**
 * Group Photos Tab Component
 * 모임 사진첩 갤러리
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Image as ImageIcon, Upload } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { MarketImageUploader } from "@/components/MarketImageUploader";
import { apiRequest } from "@/lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { useState } from "react";

interface GroupPhoto {
  id: number;
  image_url: string;
  caption: string | null;
  width: number | null;
  height: number | null;
  uploaded_by_uid: string;
  created_at: string;
}

interface GroupPhotosTabProps {
  groupId: number | null;
  isMember: boolean;
}

export function GroupPhotosTab({ groupId, isMember }: GroupPhotosTabProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [caption, setCaption] = useState("");

  // 사진 목록 조회
  const { data: photosData, isLoading } = useQuery<{ photos: GroupPhoto[]; pagination?: any }>({
    queryKey: ["/api/community/groups", groupId, "photos"],
    queryFn: async () => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await fetch(`/api/community/groups/${groupId}/photos`);
      if (!response.ok) throw new Error("Failed to fetch photos");
      return response.json();
    },
    enabled: !!groupId,
  });

  const photos = photosData?.photos || [];

  // 사진 업로드
  const uploadPhotoMutation = useMutation({
    mutationFn: async (data: { imageUrl: string; caption?: string }) => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await apiRequest(
        "POST",
        `/api/community/groups/${groupId}/photos`,
        {
          imageUrl: data.imageUrl,
          caption: data.caption || null,
        }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId, "photos"] });
      setIsUploadDialogOpen(false);
      setUploadedImageUrl("");
      setCaption("");
      toast({
        title: "업로드 완료",
        description: "사진이 성공적으로 업로드되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "업로드 실패",
        description: error.message || "사진 업로드 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <Card className="p-12 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
          <div className="h-4 bg-muted rounded w-1/3 mx-auto" />
        </div>
      </Card>
    );
  }

  const handleUpload = () => {
    if (!uploadedImageUrl) {
      toast({
        title: "이미지 필요",
        description: "사진을 선택해주세요.",
        variant: "destructive",
      });
      return;
    }

    uploadPhotoMutation.mutate({
      imageUrl: uploadedImageUrl,
      caption: caption || undefined,
    });
  };

  return (
    <div className="space-y-6">
      {/* Upload Button (Members only) */}
      {isMember && (
        <div className="flex justify-end">
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Upload className="w-4 h-4 mr-2" />
                사진 업로드
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>사진 업로드</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>사진</Label>
                  <MarketImageUploader
                    maxImages={1}
                    onImagesChange={(paths) => {
                      if (paths.length > 0) {
                        setUploadedImageUrl(paths[0]);
                      } else {
                        setUploadedImageUrl("");
                      }
                    }}
                    userId={user?.uid}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="caption">사진 설명 (선택)</Label>
                  <Textarea
                    id="caption"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="사진에 대한 설명을 입력하세요"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsUploadDialogOpen(false);
                      setUploadedImageUrl("");
                      setCaption("");
                    }}
                  >
                    취소
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={!uploadedImageUrl || uploadPhotoMutation.isPending}
                  >
                    {uploadPhotoMutation.isPending ? "업로드 중..." : "업로드"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Photos Grid */}
      {photos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo) => (
            <Card key={photo.id} className="hover-elevate cursor-pointer overflow-hidden">
              <div className="relative aspect-square">
                <img
                  src={photo.image_url}
                  alt={photo.caption || "모임 사진"}
                  className="w-full h-full object-cover"
                />
              </div>
              {photo.caption && (
                <CardContent className="p-3">
                  <p className="text-sm text-muted-foreground line-clamp-2">{photo.caption}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(photo.created_at), {
                      addSuffix: true,
                      locale: ko,
                    })}
                  </p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">등록된 사진이 없습니다</h3>
          <p className="text-muted-foreground">
            {isMember ? "첫 사진을 업로드해보세요!" : "멤버가 사진을 업로드하면 여기에 표시됩니다."}
          </p>
        </Card>
      )}
    </div>
  );
}

